import dayjs from 'dayjs';
import pluralize from 'pluralize';
import type { TrialPhase } from './types';

/**
 * Calendar days from today until `date`, counted by date boundaries rather than
 * 24-hour windows: a target later today returns 0 ("ends today"), tomorrow
 * returns 1, and a past date returns a negative count. Using calendar days (not
 * `diff(..., 'day', true)` + `Math.ceil`) avoids counting the final partial day
 * as a whole extra day, e.g. a trial ending at 18:04 today reads as "ends today"
 * instead of "1 day left".
 */
export const getCalendarDaysUntil = (date: string | Date): number =>
  dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'day');

/**
 * Maps days remaining in a trial to a visual phase.
 * Positive = days left, 0 and negative = trial ended (grace or hard stop).
 */
export const getTrialPhase = (daysRemaining: number): TrialPhase => {
  if (daysRemaining > 14) {
    return 'active_safe';
  }
  if (daysRemaining >= 7) {
    return 'active_warning';
  }
  if (daysRemaining >= 1) {
    return 'active_critical';
  }
  if (daysRemaining >= -7) {
    return 'grace_period';
  }
  return 'hard_stop';
};

/**
 * Formats days remaining as "X day(s)".
 */
export const formatDaysRemaining = (daysRemaining: number): string =>
  pluralize('day', daysRemaining, true);

/**
 * Builds the countdown text shown inside trial badges/tags: "5 days left",
 * "5d left", or "ends today".
 *
 * Once the trial end date has passed (`daysRemaining <= 0`) the backend cron job
 * that expires the trial may not have run yet, so the tag is still visible. In
 * that window we show "ends today" instead of a "0 days left"/negative count.
 */
export const getTrialDaysLabel = (
  daysRemaining: number,
  { short = false }: { short?: boolean } = {},
): string => {
  if (daysRemaining <= 0) {
    return 'ends today';
  }
  return short ? `${daysRemaining}d left` : `${formatDaysRemaining(daysRemaining)} left`;
};

/**
 * localStorage flag key recording that the trial welcome modal has been shown,
 * scoped per organization. Each org's trial shows the modal once: switching to a
 * different org (a different trial) shows it again, and returning to an org that
 * has already seen it does not. Persisted, so it holds across login and refresh.
 */
export const getTrialWelcomeSeenKey = (organizationId: string) =>
  `trialWelcomeShown:${organizationId}` as const;

export const getInactivityTitle = (graceEndsAt: string | null | undefined): string => {
  if (!graceEndsAt) {
    return 'System will become inactive';
  }
  const daysUntilAutoConversion = Math.max(0, getCalendarDaysUntil(graceEndsAt));
  if (daysUntilAutoConversion === 0) {
    return 'System becomes inactive today';
  }
  return `System becomes inactive in ${daysUntilAutoConversion} ${pluralize(
    'day',
    daysUntilAutoConversion,
  )}`;
};
